import { all, put, select, takeLeading } from 'redux-saga/effects';
import { api, apiCall, commonApiSaga } from 'redux/helpers/api';
import { authSelectors } from 'redux/auth';
import { snackbarActions } from 'redux/snackbar';
import { differenceInHours } from 'services/date-fns';
import types from './types';
import actions from './actions';
import { pulseSelectors } from '.';

function* onGetDepartments({ payload }) {
  const workspaceId = yield select(authSelectors.getWorkspaceId);

  const { ok, error, response } = yield apiCall(api.pulse.getDepartments, { workspaceId, ...payload });

  if (ok) {
    yield put(actions.getDepartmentsSuccess(response));
  } else {
    yield put(actions.getDepartmentsFailure(error));
  }
}

function* onGetLocations({ payload }) {
  const workspaceId = yield select(authSelectors.getWorkspaceId);

  const { ok, error, response } = yield apiCall(api.pulse.getLocations, { workspaceId, ...payload });

  if (ok) {
    yield put(actions.getLocationsSuccess(response));
  } else {
    yield put(actions.getLocationsFailure(error));
  }
}

function* onStorePulseSurveyResponse({ payload, config }) {
  const workspaceId = yield select(authSelectors.getWorkspaceId);

  const { ok, error } = yield apiCall(api.pulse.storePulseSurveyResponse, { ...payload, workspaceId }, config);

  if (ok) {
    yield put(actions.storePulseSurveyResponseSuccess(payload));
    yield put(snackbarActions.createSuccess('workspace.feedbackSent'));
  } else {
    yield put(actions.sendPulseFeedbackFailure(error));
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onDownloadPulseSurveyResponses({ payload }) {
  const workspaceId = yield select(authSelectors.getWorkspaceId);

  const { ok, error, response } = yield apiCall(api.pulse.downloadPulseSurveyResponses, { ...payload, workspaceId });

  if (ok) {
    yield put(actions.downloadPulseSurveyResponsesSuccess({ data: response }));
  } else {
    yield put(actions.downloadPulseSurveyResponsesFailure(error));
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onGetActivePulseSurvey({ payload, config }) {
  const activePulseSurveyFetchedAt = yield select(pulseSelectors.getActivePulseSurveyFetchedAtSelector);
  const shouldNotFetch = activePulseSurveyFetchedAt instanceof Date
  && differenceInHours(new Date(), activePulseSurveyFetchedAt) < 12;

  if (shouldNotFetch) return;

  const workspaceId = yield select(authSelectors.getWorkspaceId);

  const { ok, error, response } = yield apiCall(api.pulse.showActivePulseSurvey, { ...payload, workspaceId }, config);

  if (ok) {
    yield put(actions.getActivePulseSurveySuccess(response));
  } else {
    yield put(actions.getActivePulseSurveyFailure(error));
    yield put(snackbarActions.createFailure(error.message));
  }
}

export default function* pulseSagas() {
  yield all([
    takeLeading(types.getPulseSurveys, commonApiSaga, { apiFn: api.pulse.getPulseSurveys, workspaceId: true }),
    takeLeading(types.storePulseSurvey, commonApiSaga, {
      apiFn: api.pulse.storePulseSurvey,
      workspaceId: true,
      successMessage: 'pulse.pulseSurveyCreated',
    }),
    takeLeading(types.getPulseSurvey, commonApiSaga, {
      apiFn: api.pulse.showPulseSurvey,
      workspaceId: true,
    }),
    takeLeading(types.getActivePulseSurvey, onGetActivePulseSurvey),
    takeLeading(types.updatePulseSurvey, commonApiSaga, {
      apiFn: api.pulse.updatePulseSurvey,
      workspaceId: true,
      successMessage: 'pulse.pulseSurveyUpdated',
    }),
    takeLeading(types.patchPulseSurvey, commonApiSaga, {
      apiFn: api.pulse.patchPulseSurvey,
      workspaceId: true,
      successMessage: 'pulse.pulseSurveyUpdated',
    }),
    takeLeading(types.deletePulseSurvey, commonApiSaga, {
      apiFn: api.pulse.deletePulseSurvey,
      workspaceId: true,
      successMessage: 'pulse.pulseSurveyDeleted',
    }),
    takeLeading(types.getDepartments, onGetDepartments),
    takeLeading(types.getLocations, onGetLocations),
    takeLeading(types.getPulseSurveyOverviewAnalytics, commonApiSaga, {
      apiFn: api.pulse.showPulseSurveyOverviewAnalytics,
      workspaceId: true,
    }),
    takeLeading(types.getPulseSurveyDetailedAnalytics, commonApiSaga, {
      apiFn: api.pulse.showPulseSurveyDetailedAnalytics,
      workspaceId: true,
    }),
    takeLeading(types.getPulseScales, commonApiSaga, { apiFn: api.pulse.getPulseScales, workspaceId: true }),
    takeLeading(types.getPulseSurveyResponses, commonApiSaga, { apiFn: api.pulse.getPulseSurveyResponses, workspaceId: true }),
    takeLeading(types.storePulseSurveyResponse, onStorePulseSurveyResponse),
    takeLeading(types.downloadPulseSurveyResponses, onDownloadPulseSurveyResponses),
    takeLeading(types.getPulseSurveyPeriods, commonApiSaga, { apiFn: api.pulse.getPulseSurveyPeriods, workspaceId: true }),
    takeLeading(types.getPulseSurveySchedules, commonApiSaga, { apiFn: api.pulse.getPulseSurveySchedules, workspaceId: true }),
    takeLeading(types.storePulseSurveySchedule, commonApiSaga, {
      apiFn: api.pulse.storePulseSurveySchedule,
      workspaceId: true,
      successMessage: 'pulse.pulseSurveyScheduleCreated',
    }),
    takeLeading(types.updatePulseSurveySchedule, commonApiSaga, {
      apiFn: api.pulse.updatePulseSurveySchedule,
      workspaceId: true,
      successMessage: 'pulse.pulseSurveyScheduleUpdated',
    }),
    takeLeading(types.deletePulseSurveySchedule, commonApiSaga, {
      apiFn: api.pulse.deletePulseSurveySchedule,
      workspaceId: true,
      successMessage: 'pulse.pulseSurveyScheduleDeleted',
    }),
  ]);
}
